import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  Container,
  Grid,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Footer from '../Materials/Footer';
import BurgerBar from '../Materials/BurgerBar';

const NewsManagement = ({ isManager }) => {
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [newPostUrl, setNewPostUrl] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/linkedin-posts');
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
        const selected = data.filter(post => post.is_selected);
        setSelectedPosts(selected.map(post => post.id));
      } else {
        throw new Error('Failed to fetch posts');
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
      showSnackbar('Error loading posts', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleToggle = (postId) => () => {
    const currentIndex = selectedPosts.indexOf(postId);
    const newSelected = [...selectedPosts];

    if (currentIndex === -1) {
      if (newSelected.length >= 4) {
        showSnackbar('Maximum of 4 posts allowed', 'warning');
        return;
      }
      newSelected.push(postId);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedPosts(newSelected);
  };

  const saveSelection = async () => {
    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/linkedin-posts/selection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedPosts }),
      });

      if (response.ok) {
        showSnackbar('Selection saved successfully!', 'success');
        fetchPosts();
      } else {
        throw new Error('Failed to save selection');
      }
    } catch (error) {
      console.error("Error:", error);
      showSnackbar(error.message || 'Error saving selection', 'error');
    }
  };

  const addNewPost = async () => {
    if (!newPostUrl.includes('linkedin.com/embed/feed/update')) {
      showSnackbar('Please enter a valid LinkedIn embed URL', 'error');
      return;
    }

    try {
      const response = await fetch('https://api.hub4food.colab4food.com/api/linkedin-posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: newPostUrl }),
      });

      if (response.ok) {
        setNewPostUrl('');
        showSnackbar('Post added successfully!', 'success');
        fetchPosts();
      } else {
        throw new Error('Failed to add post');
      }
    } catch (error) {
      console.error("Error:", error);
      showSnackbar(error.message || 'Error adding post', 'error');
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Navbar */}
      <BurgerBar />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        <Typography variant="h4" gutterBottom>
          News Management
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box sx={{ 
              p: 3, 
              border: '1px solid #ccc', 
              borderRadius: '4px', 
              backgroundColor: '#f9f9f9',
              display: 'flex',
              flexDirection: 'column',
              gap: 3
            }}>
              {/* Seção de Adicionar Novo Post */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Add New LinkedIn Post
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="LinkedIn Embed URL"
                    value={newPostUrl}
                    onChange={(e) => setNewPostUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/embed/feed/update/..."
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={addNewPost}
                    disabled={!newPostUrl}
                    sx={{ height: '40px' }}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              <Divider />

              {/* Seção de Seleção de Posts */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Select Posts to Display (Max 4)
                </Typography>
                <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {posts.map((post) => (
                    <ListItem 
                      key={post.id}
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        borderRadius: '4px'
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedPosts.includes(post.id)}
                        tabIndex={-1}
                        onChange={handleToggle(post.id)}
                        disabled={selectedPosts.length >= 4 && !selectedPosts.includes(post.id)}
                      />
                      <ListItemText 
                        primary={post.title || `Post ${post.id}`} 
                        secondary={post.url} 
                        sx={{ wordBreak: 'break-word' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={saveSelection}
                  disabled={selectedPosts.length === 0}
                >
                  Save Selection
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Footer />

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsManagement;